import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Box,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { capitalizeFirstLetter, calculateAge, calculateLocalDOB } from "../shared";
import { usePredictionSocket } from "../shared/hooks/usePredictionSocket";
import { useSelector } from "react-redux";

import authFetch from "../utils/authFetch";
import SearchBar from "./common/SearchInput";

const UserPredictionTable = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Get authentication state
  const { isAuthenticated, accessToken } = useSelector((state) => state.auth);

  // Initialize socket hook (no userId initially)
  const { connect, disconnect, connected } = usePredictionSocket(null);

  const handleSearch = (value) => {
    if(page!==0) setPage(0)
    setSearch(value)
  }

  const fetchUsers = async (pageNum = 0, limit = 10, search = "") => {
    try {
      setLoading(true);
      setError(null);
      console.log("page", pageNum)
      const response = await authFetch.get(
        `${process.env.REACT_APP_BACKEND_URL}/user/admin/sandbox-customer?page=${pageNum + 1}&limit=${limit}&search=${search}`
      );

      if (response.data.success) {
        setUsers(response.data.data.sandboxCustomers || []);
        setTotalPages(response.data.data.meta?.totalPages || 1);
        // Calculate total count from totalPages and limit
        const total = (response.data.data.meta?.totalPages || 1) * (response.data.data.meta?.limit || limit);
        setTotalCount(total);
      } else {
        setError(response.data.message || "Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, rowsPerPage, search);
  }, [page, rowsPerPage, search]);

  const handleUserClick = (user) => {
    // Connect to socket with the selected user's ID
    if (isAuthenticated && accessToken && user._id) {
      // The socket connection will be handled by the UserPredictionPage component
      // We just navigate with the user data
    }

    navigate(`/prediction/${user._id}`, {
      state: { userData: user }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // if (loading) {
  //   return (
  //     <Box sx={{ p: 4, textAlign: "center" }}>
  //       <CircularProgress />
  //       <Typography variant="body1" sx={{ mt: 2 }}>Loading users...</Typography>
  //     </Box>
  //   );
  // }


  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "auto", display: "flex", flexDirection: "column", overflow: "hidden", pt: "5px" }}>

      <Box className="w-full flex-end md:w-1/2 lg:w-1/3 mb-4">
        <SearchBar
          onSearch={handleSearch}
          delay={500}
          placeholder="Search users..."
        />
      </Box>
      <Paper sx={{ boxShadow: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Fixed Header */}

        <Table sx={{ minWidth: 650, tableLayout: "fixed" }} aria-label="user prediction table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
              <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "25%", px: 2, py: 1.5 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "15%", px: 2, py: 1.5 }}>Date of birth</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "12%", px: 1, py: 1.5 }}>Time of birth</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "28%", px: 1, py: 1.5 }}>Place of birth</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "20%", px: 1, py: 1.5 }}>User Current Timezone</TableCell>
            </TableRow>
          </TableHead>
        </Table>

        {/* Scrollable Content */}
        <TableContainer
          sx={{
            overflow: "auto",
            height: "450px"
          }}
        >
          {loading ?
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>Loading users...</Typography>
            </Box>
            :
            <Table sx={{ minWidth: 650, tableLayout: "fixed" }}>
              <TableBody>
                {Array.isArray(users) && users.length > 0 ? (
                  users.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      onClick={() => handleUserClick(user)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <TableCell sx={{ textAlign: "left", width: "25%", px: 2, py: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "#e0e0e0" }}>
                            {capitalizeFirstLetter(user.name || user.email)?.charAt(0)}
                          </Avatar>
                          <Box sx={{ textAlign: "left" }}>
                            <Typography variant="body2" sx={{ fontWeight: "medium", textAlign: "left" }}>
                              {capitalizeFirstLetter(user.name || user.email)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "left" }}>
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", width: "15%", px: 2, py: 1.5 }}>
                        <Typography variant="body2" sx={{ textAlign: "left" }}>
                          {user.birthDetails?.date
                            ? (() => {
                              const localDOB = calculateLocalDOB(user.birthDetails.date, user.birthDetails.utcOffset);
                              if (!localDOB) return "N/A";
                              // Extract date part from "27 Aug 2025, 8:49 PM" format
                              return localDOB.split(', ')[0];
                            })()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", width: "12%", px: 2, py: 1.5 }}>
                        <Typography variant="body2" sx={{ textAlign: "left" }}>
                          {user.birthDetails?.date
                            ? (() => {
                              const localDOB = calculateLocalDOB(user.birthDetails.date, user.birthDetails.utcOffset);
                              if (!localDOB) return "N/A";
                              // Extract time part from "27 Aug 2025, 8:49 PM" format
                              return localDOB.split(', ')[1];
                            })()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", width: "28%", px: 2, py: 1.5, wordWrap: "break-word", whiteSpace: "normal" }}>
                        <Typography variant="body2" sx={{ textAlign: "left", wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                          {user.birthDetails?.place?.name || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", width: "20%", px: 2, py: 1.5 }}>
                        <Typography variant="body2" sx={{ textAlign: "left" }}>
                          {user.userCurrentTimezone || "N/A"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No users available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          }
        </TableContainer>

        {/* Fixed Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={Math.max(totalCount || (totalPages * rowsPerPage) || 0, users.length)}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e0e0',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            },
            '& .MuiTablePagination-selectLabel': {
              marginBottom: 0,
            },
            '& .MuiTablePagination-displayedRows': {
              marginBottom: 0,
            },
            '& .MuiTablePagination-actions': {
              marginLeft: 1,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default UserPredictionTable;
